import React, { useEffect, useImperativeHandle, useState, forwardRef, useCallback } from "react";
import {
  Megaphone,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  HelpCircle,
  Loader2,
  ExternalLink,
  RefreshCw,
} from "lucide-react";
import { listClaimLibrary } from "@/lib/api";

const TONE = {
  supported_by_bill_text: { color: "emerald", icon: CheckCircle2 },
  contradicted_by_bill_text: { color: "red", icon: XCircle },
  outside_estimate_tied_to_real_mechanism: { color: "amber", icon: AlertTriangle },
  plausible_but_not_proven: { color: "yellow", icon: HelpCircle },
  misleading_or_overstated: { color: "amber", icon: AlertTriangle },
  unsupported: { color: "red", icon: XCircle },
  unable_to_verify: { color: "stone", icon: HelpCircle },
};
const TONE_CLASSES = {
  emerald: "border-emerald-600 bg-emerald-50 text-emerald-900",
  red: "border-red-700 bg-red-50 text-red-900",
  amber: "border-amber-600 bg-amber-50 text-amber-900",
  yellow: "border-yellow-500 bg-yellow-50 text-yellow-900",
  stone: "border-stone-400 bg-stone-50 text-stone-800",
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

const PeopleAreSayingCard = forwardRef(function PeopleAreSayingCard(
  { measureDbId },
  ref
) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!measureDbId) return;
    setLoading(true);
    try {
      const data = await listClaimLibrary(measureDbId);
      setItems(data.items || []);
    } catch {
      // empty list on failure; not user-facing
    } finally {
      setLoading(false);
    }
  }, [measureDbId]);

  useImperativeHandle(ref, () => ({ refresh: load }), [load]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section
      className="paper-card-elevated rounded-sm p-5 mb-5"
      data-testid="people-are-saying-card"
    >
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-dashed border-[hsl(var(--rule))]">
        <Megaphone className="w-5 h-5 text-[hsl(var(--ink))]" />
        <div className="section-label">People Are Saying</div>
        <span className="ml-auto text-[11px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))]">
          {items.length} claim{items.length === 1 ? "" : "s"}
        </span>
      </div>

      <p className="text-xs text-[hsl(var(--ink-muted))] leading-relaxed mb-3 max-w-3xl">
        Common claims people are checking about this bill, verified against the
        official text. User-submitted claims are treated as "claims people are
        asking about" — not as verified evidence. The verdict comes from the
        bill text itself.
      </p>

      {loading && items.length === 0 && (
        <div
          className="text-sm text-[hsl(var(--ink-muted))] flex items-center gap-2"
          data-testid="people-loading"
        >
          <Loader2 className="w-4 h-4 animate-spin" /> Loading checked claims…
        </div>
      )}

      {!loading && items.length === 0 && (
        <div
          className="text-sm text-[hsl(var(--ink-muted))] italic py-3"
          data-testid="people-empty"
        >
          No public claims have been checked for this bill yet. Seen one? Paste it below.
        </div>
      )}

      <div className="space-y-3">
        {items.map((it) => {
          const tone = TONE[it.classification] || TONE.unable_to_verify;
          const Icon = tone.icon;
          return (
            <article
              key={it.id}
              className={`border-l-4 border-y border-r border-[hsl(var(--rule))] rounded-sm p-3 sm:p-4 ${TONE_CLASSES[tone.color]}`}
              data-testid={`pas-claim-${it.id}`}
            >
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <Icon className="w-4 h-4 shrink-0" />
                <span className="text-[10px] font-mono uppercase tracking-wider">
                  {it.classification_label}
                </span>
                {it.confidence && (
                  <span className="text-[10px] font-mono uppercase tracking-wider opacity-70">
                    Confidence: {it.confidence}
                  </span>
                )}
                {it.submission_count > 1 && (
                  <span className="text-[10px] font-mono uppercase tracking-wider opacity-70">
                    Asked {it.submission_count}×
                  </span>
                )}
                {it.needs_recheck && (
                  <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border border-current">
                    <RefreshCw className="w-3 h-3" />
                    Needs recheck — bill version changed
                  </span>
                )}
              </div>

              <blockquote className="text-sm italic mb-2 leading-snug" data-testid={`pas-claim-text-${it.id}`}>
                &ldquo;{it.claim}&rdquo;
              </blockquote>

              {it.plain_english_finding && (
                <p className="text-sm leading-relaxed mb-2 whitespace-pre-line">
                  {it.plain_english_finding}
                </p>
              )}

              {it.supporting_quote && (
                <blockquote className="text-xs leading-relaxed bg-white/60 border-l-2 border-current pl-2 py-1 my-2">
                  Bill text: &ldquo;{it.supporting_quote}&rdquo;
                </blockquote>
              )}

              <div className="text-[11px] mt-2 pt-2 border-t border-dashed border-current/30 flex items-center gap-2 flex-wrap">
                {it.bill_section_used && (
                  <span className="font-mono uppercase tracking-wider">
                    {it.bill_section_used}
                  </span>
                )}
                {it.last_checked_at && (
                  <span className="font-mono opacity-70">
                    Last checked {fmtDate(it.last_checked_at)}
                  </span>
                )}
                {it.claim_source_url && (
                  <a
                    href={it.claim_source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-auto inline-flex items-center gap-1 text-current underline-offset-2 hover:underline"
                  >
                    Source <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>

              {it.caveats && it.caveats.length > 0 && (
                <ul className="list-disc list-outside ml-4 mt-2 space-y-0.5 text-[11px]">
                  {it.caveats.map((c, j) => (
                    <li key={j}>{c}</li>
                  ))}
                </ul>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
});

export default PeopleAreSayingCard;
